import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { AreaPlot, LinePlot } from '@mui/x-charts/LineChart'
import { startOfQuarter } from 'date-fns/startOfQuarter'
import { differenceInDays, isSameMonth, isSameQuarter, isSameWeek, isSameYear } from 'date-fns'
import { formatDate } from 'date-fns/format'
import { formatDateYYYYMMDD } from '../packages/utils/numberFormatters'
import { useAppLocale } from '../i18n/localeUtils'
import { BarSeriesType, LineSeriesType, YAxis } from '@mui/x-charts/models'
import { ChartsSurface } from '@mui/x-charts/ChartsSurface'
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis'
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis'
import { ChartsLoadingOverlay } from '@mui/x-charts/ChartsOverlay'
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip'
import { ChartsLegend, SeriesLegendItemContext } from '@mui/x-charts/ChartsLegend'
import { useDrawingArea } from '@mui/x-charts/hooks'
import { ChartsDataProviderPro } from '@mui/x-charts-pro/ChartsDataProviderPro'
import { ChartsZoomSlider } from '@mui/x-charts-pro/ChartsZoomSlider'
import { ChartsClipPath } from '@mui/x-charts/ChartsClipPath'
import { nice } from 'd3-array'
import { BarPlot } from '@mui/x-charts/BarChart'

type GradientProps = {
    color1: string
    id: string
}

const Gradient = ({ color1, id }: GradientProps) => {
    const { top, height, bottom } = useDrawingArea()
    const svgHeight = top + bottom + height

    return (
        <defs>
            <linearGradient id={id} x1='0' x2='0' y1='0' y2={`${svgHeight}px`} gradientUnits='userSpaceOnUse'>
                <stop offset='0%' stopColor={color1} stopOpacity={1} />
                <stop offset='100%' stopColor={color1} stopOpacity={0} />
            </linearGradient>
        </defs>
    )
}

export type TimeLineChartProps = {
    children?: React.ReactNode
    hasComparison?: boolean
    height?: number
    hideTooltip?: boolean
    isLoading: boolean
    isSlim?: boolean
    series: (LineSeriesType | BarSeriesType)[]
    surfaceChildren?: ReactNode
    skipAnimation?: boolean
    xAxisData: Date[] | number[]
    yAxisValueFormatter?: YAxis['valueFormatter']
}

export const TimeLineChart = ({
    children,
    hasComparison,
    height = 300,
    hideTooltip,
    isLoading,
    isSlim,
    series,
    surfaceChildren,
    skipAnimation,
    xAxisData,
    yAxisValueFormatter,
}: TimeLineChartProps) => {
    const { dateFnsLocale, language } = useAppLocale()
    const clipPathId = React.useId()
    const [visibleSerieIds, setVisibleSerieIds] = useState(series?.map((s) => s.id))
    const containerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!series) return
        setVisibleSerieIds(series.map((s) => s.id))
    }, [series])

    const handleClickLegend = (_event: React.MouseEvent, legendItem: SeriesLegendItemContext) => {
        const seriesId = legendItem.seriesId
        const seriesToToggle = series.filter((serie) => serie.label === legendItem.label).map((s) => s.id)

        if (visibleSerieIds.includes(seriesId)) {
            setVisibleSerieIds(visibleSerieIds.filter((id) => !seriesToToggle.includes(id)))
        } else {
            setVisibleSerieIds([...visibleSerieIds, ...seriesToToggle])
        }
    }

    const internalSeries = React.useMemo(
        () =>
            series?.map(
                (s, index, arr) =>
                    ({
                        ...s,
                        data: visibleSerieIds.includes(s.id) ? s.data : [],
                        yAxisId: `y-axis-${s.type}`,

                        label: (context) => {
                            if (context === 'tooltip') {
                                return s.label
                            }

                            if (s.type === 'bar') return undefined

                            return arr.findIndex((_s) => _s.label === s.label) === index ? s.label : undefined
                        },
                    }) as LineSeriesType | BarSeriesType,
            ),
        [series, visibleSerieIds],
    )
    const minYValue = React.useMemo(() => {
        let result: number | null = null

        for (let i = 0; i < series.length; i++) {
            for (let j = 0; j < (series[i].data?.length ?? 0); j++) {
                const v = (series[i].data as number[])[j]

                if (v === null) continue

                if (result === null) {
                    result = v
                }

                if (v < result) {
                    result = v
                }
            }
        }

        return result ?? 0
    }, [series])

    const legendStyling = internalSeries
        .filter(({ id }) => !visibleSerieIds.includes(id as string))
        .reduce(
            (acc, { id }) => ({
                ...acc,
                [`[data-series=${id}]`]: {
                    opacity: 0.35,
                },
            }),
            {},
        )
    const gradientColor = series.find((s) => s.type === 'line' && s.area === true)?.color
    const isYearLabel = () => {
        const threshold = (containerRef.current?.offsetWidth ?? 0) < 800 ? 365 : 365 * 3

        return xAxisData.length > threshold
    }

    const legendHeight = 22
    const xAxisTickInterval = xAxisData.length > 365 ? 'quarter' : xAxisData.length > 60 ? 'month' : 'week'

    return (
        <div style={{ position: 'relative', height: height + legendHeight }} ref={containerRef}>
            <ChartsDataProviderPro
                height={height}
                margin={isSlim ? { top: 40, bottom: 0, left: 0, right: 0 } : undefined}
                skipAnimation={skipAnimation}
                yAxis={[
                    {
                        id: 'y-axis-line',
                        position: isSlim ? 'right' : 'left',
                        width: isSlim ? 55 : 30,
                        disableTicks: true,
                        disableLine: isSlim,
                        tickNumber: 5,
                        domainLimit: (_min, max) => {
                            const limit = nice(Math.floor(minYValue), max, 5)

                            return { min: Math.max(0, limit[0] - 2), max: limit[1] }
                        },
                        valueFormatter: yAxisValueFormatter,
                    },
                    {
                        id: 'y-axis-bar',
                        position: 'none',
                        disableTicks: true,
                        domainLimit(_min, max) {
                            return { min: 0, max: max * 5 }
                        },
                    },
                ]}
                xAxis={[
                    {
                        id: 'x-axis-line',
                        data: xAxisData,
                        scaleType: 'point',
                        tickInterval: (d, index) => {
                            const previousDate = xAxisData[index - 1]

                            if (xAxisTickInterval === 'week') {
                                return !isSameWeek(d, previousDate)
                            }

                            if (xAxisTickInterval === 'quarter') {
                                if (isYearLabel()) {
                                    return (
                                        differenceInDays(d, startOfQuarter(d)) < 60 &&
                                        !isSameYear(xAxisData[index - 1], d)
                                    )
                                }

                                return (
                                    differenceInDays(d, startOfQuarter(d)) < 30 &&
                                    !(isSameYear(d, previousDate) && isSameQuarter(d, previousDate))
                                )
                            }

                            return !isSameMonth(d, previousDate)
                        },
                        valueFormatter(value, context) {
                            if (context.location === 'zoom-slider-tooltip') {
                                return formatDateYYYYMMDD(value, { locale: language })
                            }

                            if (context.location === 'tooltip') {
                                if (hasComparison) {
                                    return formatDate(value, 'd MMM', { locale: dateFnsLocale })
                                } else {
                                    return formatDateYYYYMMDD(value, { locale: language })
                                }
                            }

                            if (xAxisTickInterval === 'week') {
                                return formatDateYYYYMMDD(value, { locale: language })
                            }

                            if (xAxisTickInterval === 'quarter') {
                                if (!isYearLabel()) {
                                    return formatDate(value, 'yyyy QQQ', { locale: dateFnsLocale })
                                }

                                return formatDate(value, 'yyyy', { locale: dateFnsLocale })
                            }

                            return formatDate(value, 'MMM', { locale: dateFnsLocale })
                        },
                    },
                    {
                        id: 'x-axis-bar',
                        data: xAxisData,
                        scaleType: 'band',
                    },
                ]}
                series={internalSeries}
            >
                <div style={{ alignItems: 'center', flexDirection: 'column', display: 'flex', height: legendHeight }}>
                    <ChartsLegend
                        direction='horizontal'
                        onItemClick={handleClickLegend}
                        sx={{
                            '.MuiChartsLegend-item': {
                                transition: 'opacity 0.5s',
                            },
                            '.MuiChartsLegend-item button': {
                                cursor: 'pointer',
                            },
                            ...legendStyling,
                        }}
                    />
                </div>
                <ChartsSurface
                    sx={{
                        '.MuiChartsAxis-root .MuiChartsAxis-line, .MuiChartsAxis-root .MuiChartsAxis-tick': {
                            stroke: 'rgba(0, 0, 0, 0.2)',
                        },
                    }}
                >
                    {isLoading && <ChartsLoadingOverlay />}
                    {!hideTooltip && <ChartsTooltip />}
                    <g clipPath={`url(#${clipPathId})`}>
                        {gradientColor && <Gradient id='areaGradient' color1={gradientColor} />}
                        <LinePlot />
                        <BarPlot />
                        <AreaPlot
                            slotProps={{
                                area: {
                                    fill: 'url(#areaGradient)',
                                },
                            }}
                        />
                        {surfaceChildren}
                    </g>
                    <ChartsXAxis axisId='x-axis-line' />
                    <ChartsXAxis axisId='x-axis-bar' />
                    <ChartsZoomSlider />
                    <ChartsClipPath id={clipPathId} offset={{ bottom: 0, left: 0, right: 0, top: 0 }} />
                    <ChartsYAxis axisId='y-axis-line' />
                    <ChartsYAxis axisId='y-axis-bar' />
                </ChartsSurface>

                {children}
            </ChartsDataProviderPro>
        </div>
    )
}
